import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

export interface IndividualConfig {
	disableTimeOut?: boolean;
	timeOut?: number;
}

export interface ToastrConfig {
	positionClass?: string;
	timeOut?: number;
}

export interface ActiveToast {
	toastId: number;
}

export const TOASTR_CONFIG = new InjectionToken<ToastrConfig>('TOASTR_CONFIG');

@Injectable({
	providedIn: 'root',
})
export class ToastrService {
	private nextId = 1;
	private toasts = new Map<number, { el: HTMLElement; timer?: number }>();
	private container: HTMLElement | null = null;
	private readonly positionClass: string;
	private readonly defaultTimeOut: number;

	constructor(@Optional() @Inject(TOASTR_CONFIG) config: ToastrConfig | null) {
		this.positionClass = config?.positionClass ?? 'toast-top-center';
		this.defaultTimeOut = config?.timeOut ?? 5000;
	}

	success(message: string, title?: string, options?: IndividualConfig): ActiveToast {
		return this.show('success', message, title, options);
	}

	error(message: string, title?: string, options?: IndividualConfig): ActiveToast {
		return this.show('error', message, title, options);
	}

	warning(message: string, title?: string, options?: IndividualConfig): ActiveToast {
		return this.show('warning', message, title, options);
	}

	info(message: string, title?: string, options?: IndividualConfig): ActiveToast {
		return this.show('info', message, title, options);
	}

	clear(toastId?: number): void {
		if (toastId == null) {
			for (const id of [...this.toasts.keys()]) {
				this.remove(id);
			}
			return;
		}
		this.remove(toastId);
	}

	remove(toastId: number): void {
		const toast = this.toasts.get(toastId);
		if (!toast) {
			return;
		}
		if (toast.timer) {
			window.clearTimeout(toast.timer);
		}
		toast.el.remove();
		this.toasts.delete(toastId);
		if (!this.toasts.size) {
			this.container?.remove();
			this.container = null;
		}
	}

	private show(type: string, message: string, title?: string, options?: IndividualConfig): ActiveToast {
		const toastId = this.nextId++;
		const el = document.createElement('div');
		el.className = `ngx-toastr toast-${type}`;
		el.setAttribute('role', 'alert');

		if (title) {
			const titleEl = document.createElement('div');
			titleEl.className = 'toast-title';
			titleEl.textContent = title;
			el.appendChild(titleEl);
		}

		const messageEl = document.createElement('div');
		messageEl.className = 'toast-message';
		messageEl.textContent = message ?? '';
		el.appendChild(messageEl);

		this.ensureContainer().appendChild(el);

		const disableTimeOut = options?.disableTimeOut === true;
		const timeOut = options?.timeOut ?? this.defaultTimeOut;
		const entry: { el: HTMLElement; timer?: number } = { el };
		if (!disableTimeOut && timeOut > 0) {
			entry.timer = window.setTimeout(() => this.remove(toastId), timeOut);
		}
		this.toasts.set(toastId, entry);
		return { toastId };
	}

	private ensureContainer(): HTMLElement {
		if (!this.container) {
			this.container = document.createElement('div');
			this.container.id = 'toast-container';
			this.container.className = `toast-container ${this.positionClass}`;
			document.body.appendChild(this.container);
		}
		return this.container;
	}
}
